import { isEmpty, isEqual, unionWith, uniqueId } from 'lodash'
import { useJobPostingProgressStatus } from '../api/getJobPostingProgressStatus'
import { View } from '@/feature/Builder'
import { Loading } from '@/components/base/Loading/Loading'
import { ScrollToTopButton } from '@/components/ScrollToTopButton'
import { useQuery } from '@/hooks/useQuery'
import {
  ScreenServiceGetByTypeFilterTypesEnum,
  type V1Component,
  V1ComponentType,
  type V1FormComponent,
  type V1FormElement,
  type V1FormElementContentState,
  type V1FormElementState,
  type V1JobPostingCardsComponent,
  type V1Screen,
  V1ScreenType,
  type V1ViewTabBar
} from 'mnvm-mypage-app-ts-if'
import ScreenByType from '@/feature/Builder/Screen/ScreenByType'
import JobPostingCards from '@/feature/Builder/Component/components/JobPostingCards/JobPostingCards'
import { Block } from '@/components/base/Block'
import spaceStyle from '@/styles/space'
import { FormElements } from '@/components/FormElement'
import { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AppContext, useApp } from '@/hooks/useApp'

export const progressStatusFilterTypesKey = 'progressStatusFilterTypes'

function JobPostingProgressStatusPage(): JSX.Element {
  const app = useApp()
  const q = useJobPostingProgressStatus()
  const query = useQuery()
  const [, setSearchParams] = useSearchParams()
  const { dispatch } = useContext(AppContext)

  const [selectedFilterTypes, setSelectedFilterTypes] = useState<
    ScreenServiceGetByTypeFilterTypesEnum[]
  >([])

  useEffect(() => {
    switch (app.screenType) {
      case V1ScreenType.JobPostingsByProgressStatusReferral:
        setSearchParams({ tab: '0' })
        break
      case V1ScreenType.JobPostingsByProgressStatusUnderConsideration:
        setSearchParams({ tab: '1' })
        break
      case V1ScreenType.JobPostingsByProgressStatusClosed:
        setSearchParams({ tab: '2' })
        break

      default:
        break
    }

    return () => {
      dispatch({
        ...app,
        screenType: ''
      })
    }
  }, [app.screenType, setSearchParams])

  useEffect(() => {
    const stateStrOrNull = sessionStorage.getItem(progressStatusFilterTypesKey)
    let tempState: ScreenServiceGetByTypeFilterTypesEnum[]
    if (stateStrOrNull != null) {
      tempState = JSON.parse(stateStrOrNull)
      setSelectedFilterTypes(tempState)
    }
  }, [])

  if (Boolean(q.isLoading) || isEmpty(q.data)) {
    return <Loading />
  }

  const tabIndex = parseInt(query.get('tab') ?? '0')

  const onFilterSearch = (
    values: ScreenServiceGetByTypeFilterTypesEnum[]
  ): void => {
    setSelectedFilterTypes(values)
    sessionStorage.setItem(progressStatusFilterTypesKey, JSON.stringify(values))
  }

  const onChangeFilter = (formElementStates: V1FormElementState[]): void => {
    let contentStates: V1FormElementContentState[] = []
    formElementStates.forEach((formElementState) => {
      if (formElementState.contentStates != null) {
        contentStates = unionWith(
          contentStates,
          formElementState.contentStates,
          isEqual
        )
      }
    })

    const filterTypes: ScreenServiceGetByTypeFilterTypesEnum[] = []
    contentStates.forEach((contentState) => {
      if (contentState.uint32 === 1) {
        filterTypes.push(
          ScreenServiceGetByTypeFilterTypesEnum.JobPostingRecommending
        )
      } else if (contentState.uint32 === 2) {
        filterTypes.push(
          ScreenServiceGetByTypeFilterTypesEnum.JobPostingConsidering
        )
      }
    })

    onFilterSearch(filterTypes)
  }

  return (
    <Block maxWidth="720px" margin={{ horizontal: spaceStyle.AUTO }}>
      <View
        key={`JobPostingProgressStatusPage_${tabIndex}`}
        view={q.data.view}
        defaultIndex={tabIndex ?? 0}
        screenBuilder={(screen: V1Screen, tabBar?: V1ViewTabBar) => {
          return [
            <ScreenByType
              key={`ScreenByType_${uniqueId().toString()}`}
              request={{
                screenType: tabBar?.screenType ?? V1ScreenType.Unspecified,
                filterTypes: !isEmpty(selectedFilterTypes)
                  ? selectedFilterTypes
                  : undefined
              }}
              componentBuilder={(component: V1Component) => {
                switch (component.type) {
                  case V1ComponentType.JobPostingScreenFilter: {
                    const formComponent = structuredClone(
                      component.component as V1FormComponent
                    )

                    return (
                      <JobPostingScreenFilterComponent
                        key={component.id}
                        initialFormElements={formComponent.elements ?? []}
                        onChangeFilter={onChangeFilter}
                      />
                    )
                  }

                  case V1ComponentType.JobPostingCards:
                    return (
                      <JobPostingCards
                        key={component.id}
                        componentId={component.id}
                        component={
                          component.component as V1JobPostingCardsComponent
                        }
                        isShowActionButton={
                          screen.type ===
                          V1ScreenType.JobPostingsByProgressStatusReferral
                        }
                        refetch={(scrollPosition: number) => {
                          if (!isEmpty(q.data))
                            window.scrollTo(0, scrollPosition)
                        }}
                        isViewMemo={true}
                      />
                    )
                  default:
                    console.log(
                      `${component.type?.toString() ?? ''} not implement component`
                    )
                    return <></>
                }
              }}
            />
          ]
        }}
      />
      <ScrollToTopButton></ScrollToTopButton>
    </Block>
  )
}

export default JobPostingProgressStatusPage

interface JobPostingScreenFilterComponentProps {
  initialFormElements: V1FormElement[]
  onChangeFilter: (formElementStates: V1FormElementState[]) => void
}

function JobPostingScreenFilterComponent({
  initialFormElements,
  onChangeFilter
}: JobPostingScreenFilterComponentProps): JSX.Element {
  const [formElements, setFormElements] =
    useState<V1FormElement[]>(initialFormElements)

  const onChange = (newFormElementState: V1FormElementState): void => {
    const newStateKey = newFormElementState.key
    if (newStateKey != null) {
      setFormElements((prev) => {
        const newState = prev.map((prevFormElement) => {
          if (prevFormElement.state?.key === newStateKey) {
            return { ...prevFormElement, state: newFormElementState }
          }
          return prevFormElement
        })
        return newState
      })

      onChangeFilter([newFormElementState])
    }
  }

  return (
    <Block padding={{ top: spaceStyle.M, bottom: spaceStyle.S }}>
      <FormElements formElementList={formElements} onChange={onChange} />
    </Block>
  )
}
